import { useEffect, useState } from 'react'
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    IconButton,
    Alert,
} from '@mui/material'
import { CloseOutlined } from '@ant-design/icons'

import { useEventBusContext } from 'services/provider/EventBusProvider'
import { stockUnitCreate } from 'models/stockUnit'
import {
    EVENT_STOCKUNIT_ADD_INIT,
    EVENT_STOCKUNIT_UPDATE_SUCCESS,
} from 'models/events/stockUnit.event'
import StockUnitForm from './StockUnit.Form'

const EMPTY_VALUES = {
    name: '',
}

const StockUnitAddDialog = () => {
    const eventbus = useEventBusContext()

    const [open, setOpen] = useState(false)
    const [values, setValues] = useState(EMPTY_VALUES)
    const [errors, setErrors] = useState({})
    const [saving, setSaving] = useState(false)
    const [generalError, setGeneralError] = useState(null)

    useEffect(() => {
        return eventbus.on(EVENT_STOCKUNIT_ADD_INIT, () => {
            setValues(EMPTY_VALUES)
            setErrors({})
            setGeneralError(null)
            setOpen(true)
        })
    }, [eventbus])

    const handleChange = (field, value) => {
        setValues((prev) => ({
            ...prev,
            [field]: value,
        }))

        setErrors((prev) => ({
            ...prev,
            [field]: undefined,
        }))
    }

    const validate = () => {
        const next = {}

        if (!values.name?.trim()) {
            next.name = 'Name is required.'
        }

        setErrors(next)

        return Object.keys(next).length === 0
    }

    const extractFieldErrors = (err) =>
        err?.response?.data?.errors || err?.errors || {}

    const handleSubmit = async () => {
        if (!validate()) return

        setSaving(true)
        setGeneralError(null)

        try {
            const res = await stockUnitCreate({
                name: values.name.trim(),
                status: 'active',
            })

            eventbus.fire(EVENT_STOCKUNIT_UPDATE_SUCCESS, res?.data)
            setOpen(false)
        } catch (err) {
            const fieldErrors = extractFieldErrors(err)

            if (Object.keys(fieldErrors).length > 0) {
                setErrors(fieldErrors)
            } else {
                setGeneralError(
                    err?.response?.data?.message ||
                    err?.message ||
                    'Failed to create stock unit.'
                )
            }
        } finally {
            setSaving(false)
        }
    }

    return (
        <Dialog
            open={open}
            onClose={() => !saving && setOpen(false)}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                Add Stock Unit

                <IconButton
                    onClick={() => !saving && setOpen(false)}
                    size="small"
                >
                    <CloseOutlined />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                {generalError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {generalError}
                    </Alert>
                )}

                <StockUnitForm
                    values={values}
                    errors={errors}
                    onChange={handleChange}
                    disabled={saving}
                />
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
                <Button
                    onClick={() => setOpen(false)}
                    disabled={saving}
                >
                    Cancel
                </Button>

                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={saving}
                >
                    {saving ? 'Saving...' : 'Create'}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default StockUnitAddDialog